/* eslint-disable max-lines-per-function */
import { useDispatch } from 'react-redux';
import { Button, Select, Stack } from '@chakra-ui/react';
import { ComponentDataIn, Components, Panel, ReferenceMap, Size, Vertex2Data } from '@rta/rta-blueprint-library';
import { DisplayMode } from '@rta/rta-blueprint-component';
import { useEffect, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { lang } from '@lang';
import { popContextPanel } from '@state';
import { resolvePanelItemGroup } from '@helpers';
import { useDesign } from '@hooks';
import { InlineNumberInput, StackedStringInput } from './controls';

/**
 * Triggers auto-distribution on a panel if SKU layer is active
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const triggerAutoDistributionIfSkuLayerActive = (panel: Panel | null, controller: any): void => {
	if (!panel) return;

	const currentDisplayMode = controller?.navigation?.displayMode;
	let shouldDistribute = false;

	switch (currentDisplayMode) {
		case DisplayMode.SkuOverhead:
		case DisplayMode.SkuOverheadManufacturing:
			shouldDistribute = true;
			break;
		default:
			shouldDistribute = false;
			break;
	}

	if (shouldDistribute) {
		setTimeout(() => {
			panel.distributeApplianceGroupsHorizontally();
		}, 0);
	}
};

type AddSpacerPanelProps = {
	id: string | null;
	position?: Vertex2Data;
	isCountertop?: boolean;
};

export const AddSpacerPanel: React.FC<AddSpacerPanelProps> = (props: AddSpacerPanelProps): JSX.Element => {
	const { id, position = { x: 0, y: 0 }, isCountertop = false } = props;

	const dispatch = useDispatch();
	const [controller] = useDesign();

	const kitchen = controller?.kitchen ?? null;

	const [refMap, setRefMap] = useState<ReferenceMap | null>(null);
	const [size, setSize] = useState<Size>({ width: 12, height: 12, depth: 3 });
	const [label, setLabel] = useState<string>('SPACER');
	const [placement, setPlacement] = useState<string>(isCountertop ? Components.PLACEMENT_TOP_ONLY : Components.PLACEMENT_FRONT_ONLY);

	useEffect(() => {
		if (kitchen === null || id === null) return;

		const map = kitchen?.getReferenceMap(id);
		if (map && map.id === id) setRefMap(map);
	}, [kitchen, id]);

	if (!kitchen || !refMap || !refMap.panel) return <></>;

	const title = lang.contextPanels.title.addSpacer;
	const isFrontOnly = placement === Components.PLACEMENT_FRONT_ONLY;

	return <ContextPanelContent title={title}>
		<Stack spacing="12px" width="100%" pt="10px">
			<StackedStringInput
				label="Label"
				placeholder="Label..."
				value={label}
				onChange={(e) => setLabel(e.target.value)}
			/>
			<Select
				size="sm"
				value={placement}
				onChange={(e) => setPlacement(e.target.value)}
			>
				<option value={Components.PLACEMENT_TOP_ONLY}>Top Only</option>
				<option value={Components.PLACEMENT_FRONT_ONLY}>Front Only</option>
			</Select>
			<InlineNumberInput
				label="Width" min={0.5}
				value={size.width}
				onChange={(valueString) => {
					setSize({ ...size, width: Number(valueString) });
				}}
			/>
			{isFrontOnly ? (
				<InlineNumberInput
					label="Height" min={0.5}
					value={size.height}
					onChange={(valueString) => {
						setSize({ ...size, height: Number(valueString) });
					}}
				/>
			) : (
				<InlineNumberInput
					label="Height" min={0.5}
					value={size.depth}
					onChange={(valueString) => {
						setSize({ ...size, depth: Number(valueString) });
					}}
				/>
			)}
			<Button
				onClick={() => {
					const category = kitchen.context.categories.find((c) => c.key === 'spacer');
					const componentData: ComponentDataIn = {
						description: label,
						size,
						cutout: size,
						sku: 'RTA-SPACE-RESERVATION',
						label,
						categoryId: category?.id,
						placement,
						msrp: 0,
						showImage: false,
						render: false
					};
					const targetGroup = resolvePanelItemGroup({ refMap, placement, fallbackX: position.x });

					if (targetGroup) {
						targetGroup.items.new({ item: { override: true, component: componentData }});
						if (targetGroup === refMap.panelItemGroup) targetGroup.centerItemsVertically();
						triggerAutoDistributionIfSkuLayerActive(refMap.panel, controller);
					}
					dispatch(popContextPanel());
				}}
			>Insert</Button>
		</Stack>
	</ContextPanelContent>;
};
